// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Center, Group, Pagination, Paper, ScrollArea, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconFilter, IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { NewTopicDialog } from '../../components/messages/NewTopicDialog';
import { HSAT_DEVICE_STATUS_LABELS, type HsatDeviceEntry, type HsatDeviceStatus } from '../../hooks/useHSATDevices';
import { patientPathPrefix } from '../patient/PatientPage.utils';

const PAGE_SIZE = 10;
const CLOUDPAT_URL = 'https://cloudpat.itamar-medical.com/';

const PENDING_STATUSES = new Set<HsatDeviceStatus | null>(['patient-approval-pending', 'hsat-ordered', null]);

function FilterableHeader({ label }: { label: string }): JSX.Element {
  return (
    <Group gap={4} wrap="nowrap" style={{ whiteSpace: 'nowrap' }}>
      <span>{label}</span>
      <IconFilter size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
    </Group>
  );
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) {
    return '—';
  }
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SectionTableProps {
  title: string;
  entries: HsatDeviceEntry[];
  isPending?: boolean;
  isActive?: boolean;
}

function SectionTable({ title, entries, isPending = false, isActive = false }: SectionTableProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [messagePatientId, setMessagePatientId] = useState<string>();
  const navigate = useNavigate();
  const filtered = useMemo(
    () =>
      search.trim()
        ? entries.filter((entry) => entry.patientName.toLowerCase().includes(search.trim().toLowerCase()))
        : entries,
    [entries, search]
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageEntries = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Paper withBorder radius="md" shadow="sm" p="lg">
      <Stack gap="md">
        <NewTopicDialog
          opened={messagePatientId !== undefined}
          subject={messagePatientId ? { reference: `Patient/${messagePatientId}` } : undefined}
          onClose={() => setMessagePatientId(undefined)}
        />
        <Group justify="space-between" align="center">
          <Title order={4}>{title}</Title>
          <TextInput
            placeholder="Search patient"
            leftSection={<IconSearch size={14} />}
            size="xs"
            w={200}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
          />
        </Group>

        {entries.length === 0 && (
          <Center h={100}>
            <Text c="dimmed" size="sm">
              No records found
            </Text>
          </Center>
        )}
        {entries.length > 0 && filtered.length === 0 && (
          <Center h={100}>
            <Text c="dimmed" size="sm">
              No matching patients
            </Text>
          </Center>
        )}

        {pageEntries.length > 0 && (
          <>
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      <FilterableHeader label="Patient" />
                    </Table.Th>
                    <Table.Th>
                      <FilterableHeader label="Ordered by" />
                    </Table.Th>
                    <Table.Th>
                      <FilterableHeader label="Date Ordered" />
                    </Table.Th>
                    {isActive && (
                      <>
                        <Table.Th>
                          <FilterableHeader label="Date Arrived" />
                        </Table.Th>
                        <Table.Th>
                          <FilterableHeader label="Activated" />
                        </Table.Th>
                      </>
                    )}
                    <Table.Th>
                      <FilterableHeader label="Status" />
                    </Table.Th>
                    {(isPending || isActive) && (
                      <Table.Th style={stickyStyle}>
                        <FilterableHeader label="Actions" />
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageEntries.map((entry) => (
                    <Table.Tr key={entry.serviceRequestId} id={`hsat-${entry.serviceRequestId}`}>
                      <Table.Td>
                        <TableAnchorButton onClick={() => navigate(patientPathPrefix(entry.patientId))}>
                          {entry.patientName}
                        </TableAnchorButton>
                      </Table.Td>
                      <Table.Td>
                        {entry.requesterName && entry.requesterId ? (
                          <TableAnchorButton onClick={() => navigate(`/Practitioner/${entry.requesterId}`)}>
                            {entry.requesterName}
                          </TableAnchorButton>
                        ) : (
                          <Text size="sm">{entry.requesterName ?? '—'}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatDate(entry.orderedDate)}</Text>
                      </Table.Td>
                      {isActive && (
                        <>
                          <Table.Td>
                            <Text size="sm">{formatDate(entry.dateArrived)}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{formatDate(entry.activatedDate)}</Text>
                          </Table.Td>
                        </>
                      )}
                      <Table.Td>
                        <Text size="sm">
                          {entry.hsatStatusDisplay ??
                            (entry.deviceStatus ? HSAT_DEVICE_STATUS_LABELS[entry.deviceStatus] : '—')}
                        </Text>
                      </Table.Td>
                      {isPending && (
                        <Table.Td style={stickyStyle}>
                          <a href={CLOUDPAT_URL} target="_blank" rel="noreferrer">
                            Go to CloudPat
                          </a>
                        </Table.Td>
                      )}
                      {isActive && (
                        <Table.Td style={stickyStyle}>
                          <Group gap="4" wrap="nowrap">
                            <TableAnchorButton onClick={() => setMessagePatientId(entry.patientId)}>
                              Message Patient
                            </TableAnchorButton>
                            <a href={CLOUDPAT_URL} target="_blank" rel="noreferrer">
                              Go to CloudPat
                            </a>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                {page} of {totalPages}
              </Text>
              <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
            </Group>
          </>
        )}
      </Stack>
    </Paper>
  );
}

const stickyStyle = {
  position: 'sticky' as const,
  right: 0,
  width: 210,
  minWidth: 210,
  background: 'var(--mantine-color-body)',
  boxShadow: '-2px 0 4px rgba(0,0,0,0.08)',
  zIndex: 1,
};

function TableAnchorButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: JSX.Element | string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ all: 'unset', cursor: 'pointer', color: 'var(--mantine-primary-color-filled)' }}
    >
      {children}
    </button>
  );
}

export function HSATDevicesView({
  entries,
  activeEntries,
}: {
  entries: HsatDeviceEntry[];
  activeEntries: HsatDeviceEntry[];
}): JSX.Element {
  const pendingEntries = useMemo(() => entries.filter((entry) => PENDING_STATUSES.has(entry.deviceStatus)), [entries]);
  return (
    <Stack gap="md">
      <SectionTable title="Pending Orders" entries={pendingEntries} isPending />
      <SectionTable title="Active Tests" entries={activeEntries} isActive />
    </Stack>
  );
}
